import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import useAuthStore from '@/store/authStore';
import apiClient from '@/lib/api';
import { Shield, CheckCircle, AlertCircle, Copy, Download } from 'lucide-react';
import { toast } from 'sonner';

const SecurityPage = () => {
  const { user, setUser } = useAuthStore();
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.totp_enabled || false);
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const handleSetup2FA = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/2fa/setup');
      setQrCode(response.data.qr_code);
      setBackupCodes(response.data.backup_codes);
      setShowSetup2FA(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to setup 2FA');
      toast.error('Failed to setup 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await apiClient.post('/2fa/enable', { code: verificationCode });
      setTwoFactorEnabled(true);
      setShowSetup2FA(false);
      setShowBackupCodes(true);
      toast.success('Two-factor authentication enabled successfully');
      
      // Update user in store
      const updatedUser = { ...user, totp_enabled: true };
      setUser(updatedUser);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
      toast.error('Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    const code = prompt('Enter your 2FA code to disable:');
    if (!code) return;

    setIsLoading(true);
    try {
      await apiClient.post('/2fa/disable', { code });
      setTwoFactorEnabled(false);
      toast.success('Two-factor authentication disabled');
      
      // Update user in store
      const updatedUser = { ...user, totp_enabled: false };
      setUser(updatedUser);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.post('/2fa/backup-codes');
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
      toast.success('Backup codes regenerated');
    } catch (err) {
      toast.error('Failed to regenerate backup codes');
    } finally {
      setIsLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied to clipboard');
  };

  const downloadBackupCodes = () => {
    const element = document.createElement('a');
    const file = new Blob([backupCodes.join('\n')], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'lostdrkc-backup-codes.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('Backup codes downloaded');
  };

  return (
    <div data-testid="security-page" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Security Center</h2>
        <p className="text-zinc-600 dark:text-zinc-400 mt-1">
          Manage your account security settings
        </p>
      </div>

      {/* Two-Factor Authentication */}
      <Card data-testid="2fa-card">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Two-Factor Authentication</CardTitle>
              <CardDescription className="mt-2">
                Add an extra layer of security to your account using an authenticator app.
              </CardDescription>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              twoFactorEnabled
                ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-400'
            }`}>
              {twoFactorEnabled ? 'Enabled' : 'Disabled'}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {twoFactorEnabled ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Two-factor authentication is enabled</span>
              </div>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={handleRegenerateBackupCodes}
                  disabled={isLoading}
                  data-testid="regenerate-codes-button"
                >
                  Regenerate Backup Codes
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDisable2FA}
                  disabled={isLoading}
                  data-testid="disable-2fa-button"
                >
                  Disable 2FA
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={handleSetup2FA}
              disabled={isLoading}
              data-testid="enable-2fa-button"
            >
              <Shield className="mr-2 h-4 w-4" />
              Enable Two-Factor Authentication
            </Button>
          )}
        </CardContent>
      </Card>

      {/* 2FA Setup Dialog */}
      <Dialog open={showSetup2FA} onOpenChange={setShowSetup2FA}>
        <DialogContent className="sm:max-w-md" data-testid="2fa-setup-dialog">
          <DialogHeader>
            <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app, then enter the verification code.
            </DialogDescription>
          </DialogHeader>
          
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {qrCode && (
              <div className="flex justify-center">
                <img src={qrCode} alt="QR Code" className="w-48 h-48" />
              </div>
            )}

            <div>
              <Label htmlFor="verification-code">Verification Code</Label>
              <Input
                id="verification-code"
                data-testid="verification-code-input"
                type="text"
                placeholder="000000"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSetup2FA(false)}
              data-testid="cancel-2fa-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEnable2FA}
              disabled={isLoading}
              data-testid="verify-enable-button"
            >
              {isLoading ? 'Verifying...' : 'Enable 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="sm:max-w-md" data-testid="backup-codes-dialog">
          <DialogHeader>
            <DialogTitle>Backup Codes</DialogTitle>
            <DialogDescription>
              Save these backup codes in a safe place. Each code can be used once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-zinc-50 dark:bg-zinc-900 p-4 rounded-lg font-mono text-sm">
              {backupCodes.map((code, index) => (
                <div key={index} className="py-1">
                  {code}
                </div>
              ))}
            </div>

            <div className="flex space-x-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={copyBackupCodes}
                data-testid="copy-codes-button"
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={downloadBackupCodes}
                data-testid="download-codes-button"
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowBackupCodes(false)} data-testid="close-codes-button">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityPage;
